import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { checkCompliance, getReadingStatus } from '../utils/complianceRanges';
import { calculateSaturationIndex, getLSIStatus } from '../utils/saturationIndex';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadingsPage = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pool, setPool] = useState(null);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    ph: '',
    chlorine: '',
    calcium_hardness: '',
    alkalinity: '',
    water_temperature: '',
    cyanuric_acid: '',
    total_dissolved_solids: '',
    flowmeter: '',
    filter_pressure: '',
    backwash_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchPoolData();
  }, [poolId]);

  const fetchPoolData = async () => {
    try {
      const [poolRes, readingsRes] = await Promise.all([
        axios.get(`${API}/pools/${poolId}`),
        axios.get(`${API}/readings?pool_id=${poolId}`)
      ]);
      setPool(poolRes.data);
      setReadings(readingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/readings`, {
        pool_id: poolId,
        ph: parseFloat(formData.ph),
        chlorine: parseFloat(formData.chlorine),
        calcium_hardness: parseFloat(formData.calcium_hardness),
        alkalinity: parseFloat(formData.alkalinity),
        water_temperature: parseFloat(formData.water_temperature),
        cyanuric_acid: parseFloat(formData.cyanuric_acid),
        total_dissolved_solids: parseFloat(formData.total_dissolved_solids),
        flowmeter: formData.flowmeter ? parseFloat(formData.flowmeter) : null,
        filter_pressure: formData.filter_pressure ? parseFloat(formData.filter_pressure) : null,
        backwash_date: formData.backwash_date || null,
        notes: formData.notes || null
      });
      toast.success(t('saved'));
      setDialogOpen(false);
      resetForm();
      fetchPoolData();
    } catch (error) {
      console.error('Failed to save reading:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this reading?')) return;
    
    try {
      await axios.delete(`${API}/readings/${readingId}`);
      toast.success(t('deleted'));
      fetchPoolData();
    } catch (error) {
      console.error('Failed to delete reading:', error);
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      ph: '',
      chlorine: '',
      calcium_hardness: '',
      alkalinity: '',
      water_temperature: '',
      cyanuric_acid: '',
      total_dissolved_solids: '',
      flowmeter: '',
      filter_pressure: '',
      backwash_date: '',
      notes: ''
    });
  };

  // Prepare chart data
  const chartData = readings.slice(0, 90).reverse().map(r => ({
    date: new Date(r.date).toLocaleDateString(),
    pH: r.ph,
    Chlorine: r.chlorine,
    Alkalinity: r.alkalinity
  }));

  // Compliance Badge Component
  const ComplianceBadge = ({ reading }) => {
    const status = getReadingStatus(reading);
    
    if (status.compliant) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Compliant
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Out of Range
      </span>
    );
  };

  // Value with indicator
  const ValueWithIndicator = ({ parameter, value }) => {
    const compliance = checkCompliance(parameter, value);
    
    if (compliance.status === 'none' || compliance.status === 'compliant') {
      return <span className="font-mono">{value}</span>;
    }
    
    return (
      <span className={`font-mono font-medium ${
        compliance.status === 'low' ? 'text-amber-600' : 'text-red-600'
      }`}>
        {value}
      </span>
    );
  };

  // LSI Badge Component
  const LSIBadge = ({ reading }) => {
    const lsi = calculateSaturationIndex(reading);
    const status = getLSIStatus(lsi);
    
    return (
      <div className="flex items-center space-x-2">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${status.color}`}>
          {status.label}
        </span>
        <span className="font-mono text-sm font-medium">
          {lsi !== null ? lsi.toFixed(2) : 'N/A'}
        </span>
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="readings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/pools')}
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-secondary tracking-tight">{pool?.pool_name}</h1>
            <p className="text-muted-foreground mt-1">{pool?.property_name}</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-reading-dialog-button" className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>{t('addReading')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('addReading')}</DialogTitle>
              <DialogDescription>Record daily chemical readings</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ph">{t('ph')} (7.2-7.8)</Label>
                  <Input
                    id="ph"
                    data-testid="ph-input"
                    type="number"
                    step="0.1"
                    value={formData.ph}
                    onChange={(e) => setFormData({ ...formData, ph: e.target.value })}
                    className={formData.ph && checkCompliance('ph', formData.ph).status !== 'compliant' && checkCompliance('ph', formData.ph).status !== 'none' ? 'border-amber-500' : ''}
                    required
                  />
                  {formData.ph && checkCompliance('ph', formData.ph).status !== 'compliant' && checkCompliance('ph', formData.ph).status !== 'none' && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {checkCompliance('ph', formData.ph).message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chlorine">{t('chlorine')} (2-4 ppm)</Label>
                  <Input
                    id="chlorine"
                    data-testid="chlorine-input"
                    type="number"
                    step="0.1"
                    value={formData.chlorine}
                    onChange={(e) => setFormData({ ...formData, chlorine: e.target.value })}
                    className={formData.chlorine && checkCompliance('chlorine', formData.chlorine).status !== 'compliant' && checkCompliance('chlorine', formData.chlorine).status !== 'none' ? 'border-amber-500' : ''}
                    required
                  />
                  {formData.chlorine && checkCompliance('chlorine', formData.chlorine).status !== 'compliant' && checkCompliance('chlorine', formData.chlorine).status !== 'none' && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {checkCompliance('chlorine', formData.chlorine).message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calcium_hardness">{t('calciumHardness')} (200-400 ppm)</Label>
                  <Input
                    id="calcium_hardness"
                    data-testid="calcium-hardness-input"
                    type="number"
                    step="1"
                    value={formData.calcium_hardness}
                    onChange={(e) => setFormData({ ...formData, calcium_hardness: e.target.value })}
                    className={formData.calcium_hardness && checkCompliance('calcium_hardness', formData.calcium_hardness).status !== 'compliant' && checkCompliance('calcium_hardness', formData.calcium_hardness).status !== 'none' ? 'border-amber-500' : ''}
                    required
                  />
                  {formData.calcium_hardness && checkCompliance('calcium_hardness', formData.calcium_hardness).status !== 'compliant' && checkCompliance('calcium_hardness', formData.calcium_hardness).status !== 'none' && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {checkCompliance('calcium_hardness', formData.calcium_hardness).message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alkalinity">{t('alkalinity')} (80-120 ppm)</Label>
                  <Input
                    id="alkalinity"
                    data-testid="alkalinity-input"
                    type="number"
                    step="1"
                    value={formData.alkalinity}
                    onChange={(e) => setFormData({ ...formData, alkalinity: e.target.value })}
                    className={formData.alkalinity && checkCompliance('alkalinity', formData.alkalinity).status !== 'compliant' && checkCompliance('alkalinity', formData.alkalinity).status !== 'none' ? 'border-amber-500' : ''}
                    required
                  />
                  {formData.alkalinity && checkCompliance('alkalinity', formData.alkalinity).status !== 'compliant' && checkCompliance('alkalinity', formData.alkalinity).status !== 'none' && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {checkCompliance('alkalinity', formData.alkalinity).message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="water_temperature">{t('waterTemp')}</Label>
                  <Input
                    id="water_temperature"
                    data-testid="water-temp-input"
                    type="number"
                    step="0.1"
                    value={formData.water_temperature}
                    onChange={(e) => setFormData({ ...formData, water_temperature: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cyanuric_acid">{t('cyanuricAcid')} (30-50 ppm)</Label>
                  <Input
                    id="cyanuric_acid"
                    data-testid="cyanuric-acid-input"
                    type="number"
                    step="1"
                    value={formData.cyanuric_acid}
                    onChange={(e) => setFormData({ ...formData, cyanuric_acid: e.target.value })}
                    className={formData.cyanuric_acid && checkCompliance('cyanuric_acid', formData.cyanuric_acid).status !== 'compliant' && checkCompliance('cyanuric_acid', formData.cyanuric_acid).status !== 'none' ? 'border-amber-500' : ''}
                    required
                  />
                  {formData.cyanuric_acid && checkCompliance('cyanuric_acid', formData.cyanuric_acid).status !== 'compliant' && checkCompliance('cyanuric_acid', formData.cyanuric_acid).status !== 'none' && (
                    <p className="text-xs text-amber-600 flex items-center">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {checkCompliance('cyanuric_acid', formData.cyanuric_acid).message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_dissolved_solids">{t('tds')}</Label>
                  <Input
                    id="total_dissolved_solids"
                    data-testid="tds-input"
                    type="number"
                    step="1"
                    value={formData.total_dissolved_solids}
                    onChange={(e) => setFormData({ ...formData, total_dissolved_solids: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="flowmeter">Flowmeter (GPM)</Label>
                  <Input
                    id="flowmeter"
                    data-testid="flowmeter-input"
                    type="number"
                    step="0.1"
                    value={formData.flowmeter}
                    onChange={(e) => setFormData({ ...formData, flowmeter: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="filter_pressure">Filter Pressure (PSI)</Label>
                  <Input
                    id="filter_pressure"
                    data-testid="filter-pressure-input"
                    type="number"
                    step="0.1"
                    value={formData.filter_pressure}
                    onChange={(e) => setFormData({ ...formData, filter_pressure: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="backwash_date">{t('backwashDate')}</Label>
                  <Input
                    id="backwash_date"
                    data-testid="backwash-date-input"
                    type="text"
                    value={formData.backwash_date}
                    onChange={(e) => setFormData({ ...formData, backwash_date: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="notes">{t('notes')}</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              {/* LSI Preview */}
              {formData.ph && formData.water_temperature && formData.calcium_hardness && formData.alkalinity && formData.total_dissolved_solids && (
                <div className="border rounded-lg p-4 bg-accent/50">
                  <h4 className="font-medium text-sm mb-2">Saturation Index Preview</h4>
                  {(() => {
                    const lsi = calculateSaturationIndex({
                      ph: formData.ph,
                      water_temperature: formData.water_temperature,
                      calcium_hardness: formData.calcium_hardness,
                      alkalinity: formData.alkalinity,
                      total_dissolved_solids: formData.total_dissolved_solids
                    });
                    const status = getLSIStatus(lsi);
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded text-sm font-medium border ${status.color}`}>
                            {status.label}
                          </span>
                          <span className="font-mono text-lg font-bold">
                            LSI: {lsi !== null ? lsi.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{status.description}</p>
                        {status.recommendation && (
                          <p className="text-sm font-medium">{status.recommendation}</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="flex space-x-2">
                <Button type="submit" data-testid="reading-submit-button" className="flex-1">{t('save')}</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Chart */}
      {readings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Chemical Trends</CardTitle>
            <CardDescription>Last 90 days of readings</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="pH" stroke="#0091FF" strokeWidth={2} />
                <Line type="monotone" dataKey="Chlorine" stroke="#10B981" strokeWidth={2} />
                <Line type="monotone" dataKey="Alkalinity" stroke="#F59E0B" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Readings Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('readings')}</CardTitle>
          <CardDescription>All recorded measurements</CardDescription>
        </CardHeader>
        <CardContent>
          {readings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">{t('noData')}</p>
              <Button onClick={() => setDialogOpen(true)}>{t('addReading')}</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-accent">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">{t('date')}</th>
                    <th className="px-4 py-3 text-left font-medium">Compliance</th>
                    <th className="px-4 py-3 text-left font-medium">LSI</th>
                    <th className="px-4 py-3 text-left font-medium">{t('ph')}</th>
                    <th className="px-4 py-3 text-left font-medium">{t('chlorine')}</th>
                    <th className="px-4 py-3 text-left font-medium">Ca Hard.</th>
                    <th className="px-4 py-3 text-left font-medium">{t('alkalinity')}</th>
                    <th className="px-4 py-3 text-left font-medium">Temp</th>
                    <th className="px-4 py-3 text-left font-medium">CYA</th>
                    <th className="px-4 py-3 text-left font-medium">TDS</th>
                    <th className="px-4 py-3 text-left font-medium">Flow</th>
                    <th className="px-4 py-3 text-left font-medium">Pressure</th>
                    <th className="px-4 py-3 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.id} className="border-b hover:bg-accent/50">
                      <td className="px-4 py-3 font-mono">
                        {new Date(reading.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <ComplianceBadge reading={reading} />
                      </td>
                      <td className="px-4 py-3">
                        <LSIBadge reading={reading} />
                      </td>
                      <td className="px-4 py-3">
                        <ValueWithIndicator parameter="ph" value={reading.ph} />
                      </td>
                      <td className="px-4 py-3">
                        <ValueWithIndicator parameter="chlorine" value={reading.chlorine} />
                      </td>
                      <td className="px-4 py-3">
                        <ValueWithIndicator parameter="calcium_hardness" value={reading.calcium_hardness} />
                      </td>
                      <td className="px-4 py-3">
                        <ValueWithIndicator parameter="alkalinity" value={reading.alkalinity} />
                      </td>
                      <td className="px-4 py-3 font-mono">{reading.water_temperature}</td>
                      <td className="px-4 py-3">
                        <ValueWithIndicator parameter="cyanuric_acid" value={reading.cyanuric_acid} />
                      </td>
                      <td className="px-4 py-3 font-mono">{reading.total_dissolved_solids}</td>
                      <td className="px-4 py-3 font-mono">{reading.flowmeter || '-'}</td>
                      <td className="px-4 py-3 font-mono">{reading.filter_pressure || '-'}</td>
                      <td className="px-4 py-3">
                        <Button
                          data-testid={`delete-reading-${reading.id}`}
                          onClick={() => handleDelete(reading.id)}
                          variant="ghost"
                          size="sm"
                          className="text-danger"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReadingsPage;